import { logger } from '@adornis/base/logging.js';
import { type ValueEvent } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { upsertLeadOrContactAndCheckDOI } from '@adornis/digitale-helden-shared/api/Lead/upsertLeadOrContactAndCheckDOI.js';
import '@adornis/digitale-helden-shared/client/theme/d-button.js';
import '@adornis/digitale-helden-shared/client/theme/d-checkbox.js';
import { DubniumDialog } from '@adornis/digitale-helden-shared/client/theme/d-dialog.js';
import '@adornis/digitale-helden-shared/client/theme/d-dropdown-selection.js';
import '@adornis/digitale-helden-shared/client/theme/d-textarea.js';
import { Lead } from '@adornis/digitale-helden-shared/db/Lead.js';
import { UpsertContactOptions } from '@adornis/digitale-helden-shared/db/UpsertContactOptions.js';
import { mobile } from '@adornis/digitale-helden-shared/db/enums.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import { clientTranslator } from '@adornis/translation-core/client/clientTranslator.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { sendMediaPackageMail } from '../db/sendMediaPackageMail';

@customElement('las-form-media-package')
export class LASFormMediaPackage extends ChemistryLitElement {
  @state() private formController = this._initFormController();
  @state() private repeatEmail: string | undefined = undefined;
  @state() private _acceptedPrivacy = false;

  private _issue = '';
  private _company = '';

  private readonly _size = new WindowSizeController(this);

  override render() {
    if (!this.formController.document) return html`<x-loading-state></x-loading-state>`;
    const doc = this.formController.document;

    return html`
      <d-flex space="lg">
        <d-grid space="md" columns=${(this._size.value?.width ?? 0) <= mobile ? '1' : '2'}>
          <!-- Vorname -->
          <d-input
            ${this.formController.field('firstName')}
            placeholder=${clientTranslator.translate('first_name')}
            placeholder-mode="static-floating"
            flex
          ></d-input>

          <!-- Nachname -->
          <d-input
            ${this.formController.field('lastName')}
            placeholder-mode="static-floating"
            placeholder=${clientTranslator.translate('last_name')}
            flex
          ></d-input>

          <d-input
            ${this.formController.field('email')}
            placeholder-mode="static-floating"
            placeholder=${clientTranslator.translate('e_mail')}
            @value-picked=${() => this.requestUpdate()}
          ></d-input>

          <d-input
            placeholder-mode="static-floating"
            .value=${this.repeatEmail}
            placeholder=${clientTranslator.translate('e_mail_repeat')}
            onpaste="return false"
            ondrop="return false"
            @value-picked=${(e: ValueEvent<string>) => (this.repeatEmail = e.detail.value)}
            error=${ifDefined(
              this.formController.document.email === this.repeatEmail || this.repeatEmail === undefined
                ? undefined
                : clientTranslator.translate('email_repeat_error'),
            )}
          ></d-input>

          <!-- company infos -->
          <d-input
            placeholder=${clientTranslator.translate('media_package_form', 'company')}
            placeholder-mode="static-floating"
            @value-changed=${(e: ValueEvent<string>) => (this._company = e.detail.value)}
          ></d-input>

          ${this._isMobile() ? nothing : html`<div></div>`}

          <d-flex ${css({ gridColumn: this._size.mobile ? '1 / 2' : '1 / 3' })}>
            <d-textarea
              placeholder=${clientTranslator.translate('media_package_form', 'concern')}
              placeholder-mode="static-floating"
              @value-changed=${(e: ValueEvent<string>) => {
                this._issue = e.detail.value;
              }}
            ></d-textarea>
          </d-flex>
        </d-grid>

        <d-flex horizontal crossaxis-center space="xs">
          <d-checkbox
            .value=${this._acceptedPrivacy}
            @value-picked=${(e: ValueEvent<boolean>) => {
              this._acceptedPrivacy = e.detail.value;
            }}
          ></d-checkbox>
          <d-text> ${clientTranslator.translate('accepts_data_privacy')} </d-text>
        </d-flex>

        <d-flex space="md">
          <d-text bold> ${clientTranslator.translate('e_mail_accept_header')} </d-text>
          <d-checkbox
            .label=${clientTranslator.translate('e_mail_hero_accept')}
            ${this.formController.field('isNewsletter')}
          ></d-checkbox>
          <d-checkbox
            .label=${clientTranslator.translate('e_mail_webinar_accept')}
            ${this.formController.field('isWebinar')}
          ></d-checkbox>
        </d-flex>
        <d-button
          ${css({ marginRight: 'auto' })}
          @click=${() => this.formController.validate()}
          ?disabled=${!this._acceptedPrivacy}
        >
          ${clientTranslator.translate('request')}
        </d-button>
      </d-flex>
    `;
  }

  private _isMobile() {
    return (this._size.value?.width ?? 0) <= mobile;
  }

  private _initFormController() {
    this._acceptedPrivacy = false;
    this.repeatEmail = undefined;
    this._company = '';
    this._issue = '';
    return new FormController(this, new Lead({}), {
      success: async (data: Lead) => {
        if (!this.repeatEmail || this.repeatEmail !== data.email) {
          return XSnackbar.show(clientTranslator.translate('email_repeat_error'));
        }

        const action = async () => {
          data.communicationCampaigns = [clientTranslator.translate('media_package_form', 'media_package')];

          await upsertLeadOrContactAndCheckDOI(
            data,
            false,
            new UpsertContactOptions({
              checkCommunicationCampaigns: true,
            }),
          )();

          if (this._issue) {
            await sendMediaPackageMail(this._issue, data, this._company)();
          }
        };

        return await DubniumDialog.waitFor(
          action(),
          clientTranslator.translate('loading', 'subscribing_media_package'),
          {
            closeOnNavigate: false,
          },
        ).then(() => {
          this.formController = this._initFormController();
          XSnackbar.show('Formular abgeschickt');
        });
      },
      error: err => {
        logger.error(err, 'error in form controller');
      },
      translateError: key => clientTranslator.translate(key),
    });
  }
}
